import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Checkbox,
  Chip,
  SearchBar,
  Select,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { SearchableSelect } from "@/components/Form/SearchableSelect";
import { AsyncSearchableSelect } from "@/components/Form/AsyncSearchableSelect";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import EmptyState from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import LoadingState from "@/components/LoadingState";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { DetailsColumn } from "./components/DetailsColumn";
import { LinkContactModal } from "./components/LinkContactModal";
import { OrderColumn } from "./components/OrderColumn";
import { StatusColumn } from "./components/StatusColumn";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useUrlParams } from "@/hooks/useUrlParams";
import { useFilters } from "@/hooks/useFilters";
import { usePublications } from "@/hooks/usePublications";
import { useQuery } from "@/hooks/useApi";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { usePermissions } from "@/hooks/usePermissions";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { useTestimonialUniques } from "@/hooks/useTestimonialUniques";
import { downloadBlob } from "@/utils/csvExport";
import { publicationOptionLabel } from "@/utils/taxonomyFilter";
import { editorialCategories } from "@/api/editorialCategories.service";
import { firms as firmsService } from "@/api/firms.service";
import { contacts as contactsService } from "@/api/contacts.service";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import type { TaxonomyMode } from "@/api/submissions.service";
import {
  pipelineService,
  type Testimonial,
  type UnlinkTestimonialType,
} from "@/api/pipeline.service";
import { TESTIMONIALS_UNLINK_LAWYER_PERMISSION } from "@/constants/permissions";
import { contentTypeOptions } from "../contentTypeOptions";
import {
  axisValueFitsMode,
  buildEffectiveTestimonialFilters,
  buildTestimonialExportRequest,
  selectedAxisValue,
  TESTIMONIAL_AXES,
} from "./effectiveFilters";
import styles from "./Testimonials.module.css";

const EXPORT_FLAG = "approved-testimonials-export";
const EXPORT_PERMISSION = "staff.approval.read";
const ORDER_WRITE_PERMISSIONS = ["staff.approval.write", "developer.job_runs"];

function TestimonialsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchParams, updateUrl } = useUrlParams();
  const { toggleVisible, mode, taxonomy, ready } = useTaxonomyMode();
  const inLegacyMode = ready && mode === "legacy";
  const inNewMode = ready && mode === "new";
  const currentYear = new Date().getFullYear();

  const [filters, setFilters] = useState({
    edition: searchParams.get("edition") || String(currentYear),
    publication: searchParams.get("publication") || "all",
    region: searchParams.get("region") || "all",
    practice: searchParams.get("practice") || "all",
    firm: searchParams.get("firm") || "all",
    contact: searchParams.get("contact") || "all",
    status: searchParams.get("status") || "all",
    type: searchParams.get("type") || "all",
    language: searchParams.get("language") || "all",
    sentiment: searchParams.get("sentiment") || "all",
    search: searchParams.get("search") || "",
  });

  const [selectedFirmName, setSelectedFirmName] = useState<string | null>(
    searchParams.get("firmLabel"),
  );
  const contactParam = searchParams.get("contact");
  const [selectedContactName, setSelectedContactName] = useState<string | null>(
    contactParam && contactParam !== "all" ? contactParam : null,
  );
  const firmOptionsRef = useRef<{ value: number; label: string }[]>([]);

  const [regionOptionsState, setRegionOptionsState] = useState<{ value: string; label: string }[]>([
    { value: "all", label: "Select publication first" },
  ]);
  const [practiceOptionsState, setPracticeOptionsState] = useState<
    { value: string; label: string }[]
  >([{ value: "all", label: "Select region first" }]);
  const [legacyNoRegionsForPublication, setLegacyNoRegionsForPublication] = useState(false);

  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 25);
  const [sortField] = useState(searchParams.get("sortField") || "");
  const [sortDirection] = useState<"asc" | "desc">(
    (searchParams.get("sortDirection") as "asc" | "desc") || "desc",
  );

  const [linkingContactItem, setLinkingContactItem] = useState<Testimonial | null>(null);

  const [data, setData] = useState<{
    items: Testimonial[];
    pagination: { total: number; page: number; pageSize: number; hasMore: boolean };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Debounce ref for search input
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
  });

  useEffect(() => {
    if (!ready) return;
    const stale = TESTIMONIAL_AXES.filter(axis => !axisValueFitsMode(axis, filters[axis], mode));
    if (stale.length === 0) return;
    const cleared = Object.fromEntries(stale.map(axis => [axis, "all"]));
    setFilters(prev => ({ ...prev, ...cleared }));
    updateUrl(cleared);
  }, [ready, mode, filters.publication, filters.region, filters.practice]);

  const legacyPublication = inLegacyMode
    ? selectedAxisValue("publication", filters.publication, "legacy")
    : undefined;
  const publicationId =
    legacyPublication === undefined ? undefined : Number.parseInt(legacyPublication, 10);
  const legacyRegion =
    publicationId === undefined ? undefined : selectedAxisValue("region", filters.region, "legacy");
  const regionId = legacyRegion === undefined ? undefined : Number.parseInt(legacyRegion, 10);

  const { data: publicationsData } = usePublications(
    { edition: filters.edition },
    { enabled: inLegacyMode },
  );

  const { data: languagesData } = useQuery(() => pipelineService.getTestimonialLanguages(), []);

  // Load regions based on publication
  useEffect(() => {
    if (!inLegacyMode) return;
    if (publicationId === undefined) {
      setRegionOptionsState([{ value: "all", label: "Select publication first" }]);
      setLegacyNoRegionsForPublication(false);
      return;
    }

    const loadRegions = async () => {
      try {
        const resp = await editorialCategories.getAll({
          publicationId,
          type: "country",
          limit: 100,
        });
        const options =
          resp.editorialCategories?.map(cat => ({
            value: String(cat.id),
            label:
              (cat as unknown as { country?: string }).country ||
              (cat as unknown as { name?: string }).name ||
              `Region ${cat.id}`,
          })) ?? [];
        if (options.length === 0) {
          setLegacyNoRegionsForPublication(true);
        } else {
          setLegacyNoRegionsForPublication(false);
        }
        setRegionOptionsState([{ value: "all", label: "All regions" }, ...options]);
      } catch (_e) {
        setRegionOptionsState([{ value: "all", label: "Failed to load regions" }]);
      }
    };

    loadRegions();
  }, [inLegacyMode, publicationId]);

  // Load practice areas based on region
  useEffect(() => {
    if (!inLegacyMode) return;
    if (publicationId === undefined || (regionId === undefined && !legacyNoRegionsForPublication)) {
      setPracticeOptionsState([{ value: "all", label: "Select region first" }]);
      return;
    }

    const loadPracticeAreas = async () => {
      try {
        // Page through all practice areas (backend caps `limit` at 100 per request).
        const allPracticeAreas = await editorialCategories.getAllPracticeAreas({
          publicationId,
          // When the publication has no regions, load all practice areas without countryId
          ...(legacyNoRegionsForPublication ? {} : { countryId: regionId }),
        });
        const options = allPracticeAreas
          .map((cat, index) => {
            const ec = cat as unknown as {
              id: number;
              practiceGroup?: string;
              practiceArea?: string;
              tableName?: string;
              name?: string;
            };

            const parts: string[] = [];
            if (ec.practiceGroup) parts.push(ec.practiceGroup);
            if (ec.practiceArea) parts.push(ec.practiceArea);
            if (ec.tableName) parts.push(ec.tableName);

            // Remove consecutive duplicates, e.g. "Corporate > Corporate"
            const deduped: string[] = [];
            for (const part of parts) {
              if (deduped.length === 0 || deduped[deduped.length - 1] !== part) {
                deduped.push(part);
              }
            }

            let label: string;
            if (deduped.length === 0) {
              label = ec.name || `Practice ${ec.id}`;
            } else if (deduped.length === 1) {
              label = deduped[0];
            } else {
              label = deduped.join(" > ");
            }

            return {
              value: `${ec.id}:${index}`,
              label,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setPracticeOptionsState([{ value: "all", label: "All practices" }, ...options]);
      } catch (_e) {
        setPracticeOptionsState([{ value: "all", label: "Failed to load practices" }]);
      }
    };

    loadPracticeAreas();
  }, [inLegacyMode, publicationId, regionId, legacyNoRegionsForPublication]);

  const debouncedSearch = useDebouncedValue(filters.search);

  const newEditionId = inNewMode
    ? selectedAxisValue("publication", filters.publication, "new")
    : undefined;
  const newRankingEntityId =
    newEditionId === undefined ? undefined : selectedAxisValue("region", filters.region, "new");

  const { data: publicationUniques, error: publicationUniquesError } = useTestimonialUniques(
    "publication",
    {},
    inNewMode,
  );
  const { data: regionUniques, error: regionUniquesError } = useTestimonialUniques(
    "region",
    { editionId: newEditionId },
    newEditionId !== undefined,
  );
  const noRegionsForPublication =
    mode === "new"
      ? newEditionId !== undefined && regionUniques?.values.length === 0
      : legacyNoRegionsForPublication;
  const { data: practiceUniques, error: practiceUniquesError } = useTestimonialUniques(
    "practice",
    { editionId: newEditionId, rankingEntityId: newRankingEntityId },
    newEditionId !== undefined && (newRankingEntityId !== undefined || noRegionsForPublication),
  );

  const effectiveFilters = useMemo(
    () =>
      buildEffectiveTestimonialFilters({
        mode,
        taxonomy,
        filters: {
          publication: filters.publication,
          region: filters.region,
          practice: filters.practice,
          firm: filters.firm,
          contact: filters.contact,
          type: filters.type,
          language: filters.language,
          sentiment: filters.sentiment,
        },
        regionOptions: regionOptionsState,
        noRegionsForPublication,
        debouncedSearch,
      }),
    [
      mode,
      taxonomy,
      filters.publication,
      filters.region,
      filters.practice,
      filters.firm,
      filters.contact,
      filters.type,
      filters.language,
      filters.sentiment,
      regionOptionsState,
      noRegionsForPublication,
      debouncedSearch,
    ],
  );

  const fetchData = useCallback(
    async (signal: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const result = await pipelineService.getTestimonials(
          {
            ...effectiveFilters,
            page: currentPage,
            pageSize: itemsPerPage,
            status: filters.status !== "all" ? filters.status : undefined,
            sortField: sortField || undefined,
            sortDirection: sortField ? sortDirection : undefined,
          },
          signal,
        );
        if (!signal.aborted) {
          setData(result);
        }
      } catch (err) {
        if (!signal.aborted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [effectiveFilters, currentPage, itemsPerPage, filters.status, sortField, sortDirection],
  );

  useEffect(() => {
    if (!ready) return;
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData, reloadToken, ready]);

  // The detail page hands over its outcome message on the way back; the state
  // is cleared so a reload does not repeat it.
  useEffect(() => {
    const message = location.state?.toast;
    if (typeof message !== "string") return;
    setToast({ message, type: "success" });
    navigate(location.pathname + location.search, { replace: true });
  }, [location.state, location.pathname, location.search, navigate]);

  const { hasFlag } = useFeatureFlags();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canExport = hasFlag(EXPORT_FLAG) && hasPermission(EXPORT_PERMISSION);
  const canEditOrder = hasAnyPermission(ORDER_WRITE_PERMISSIONS);
  const canUnlinkLawyer = hasPermission(TESTIMONIALS_UNLINK_LAWYER_PERMISSION);
  const [visibleOnly, setVisibleOnly] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportInFlightRef = useRef(false);

  const selectedPublication = publicationsData?.publications?.find(
    (p: LegacyPublication) => p.id === effectiveFilters.publicationId,
  );
  const selectedEdition = publicationUniques?.values.find(
    value => value.editionId === effectiveFilters.editionId,
  );
  const regionPending =
    mode === "legacy" &&
    filters.publication !== "all" &&
    filters.region !== "all" &&
    effectiveFilters.regionName === undefined;
  const exportRequest = regionPending
    ? null
    : buildTestimonialExportRequest(effectiveFilters, {
        visibleOnly,
        firmName: selectedFirmName,
        publicationName:
          mode === "new" ? selectedEdition?.publicationName : selectedPublication?.publication,
        edition:
          (mode === "new" ? selectedEdition?.edition : selectedPublication?.edition) ??
          filters.edition,
      });
  const exportNeedsSelection =
    effectiveFilters.companyId === undefined ||
    (mode === "new" ? effectiveFilters.editionId : effectiveFilters.publicationId) === undefined;

  const handleExport = async () => {
    if (!exportRequest || exportInFlightRef.current) return;
    exportInFlightRef.current = true;
    setIsExporting(true);
    setExportError(null);
    try {
      const { blob, filename } = await pipelineService.exportTestimonials(exportRequest);
      downloadBlob(blob, filename);
    } catch (exportFailure) {
      setExportError(
        exportFailure instanceof Error && exportFailure.message
          ? exportFailure.message
          : "Failed to export testimonials",
      );
    } finally {
      exportInFlightRef.current = false;
      setIsExporting(false);
    }
  };

  const testimonials = data?.items ?? [];
  const pagination = data?.pagination ?? {
    total: 0,
    page: 1,
    pageSize: itemsPerPage,
    hasMore: false,
  };

  const handleRowClick = (row: Testimonial) => {
    navigate(`/research/testimonials/${row.id}`, {
      state: { returnSearch: location.search },
    });
  };

  const handleTextFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      updateUrl({ [key]: value, page: "1" });
    }, 400);
  };

  // Cascade filter change handler (copied from ContentInbox)
  const handleFilterChangeWithDeps = (key: string, value: string | number | boolean) => {
    setCurrentPage(1);
    const stringValue = String(value);

    if (key === "edition") {
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        edition: stringValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
      }));
      updateUrl({
        edition: stringValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        page: "",
      });
      return;
    }

    if (key === "publication") {
      setLegacyNoRegionsForPublication(false);
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        publication: stringValue,
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
      }));
      updateUrl({
        publication: stringValue,
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        page: "",
      });
      return;
    }

    if (key === "region") {
      if (filters.publication === "all") {
        setFilters(prev => ({ ...prev, region: "all" }));
        updateUrl({ region: "all" });
      } else {
        setSelectedFirmName(null);
        setSelectedContactName(null);
        setFilters(prev => ({
          ...prev,
          region: stringValue,
          practice: "all",
          firm: "all",
          contact: "all",
        }));
        updateUrl({
          region: stringValue,
          practice: "all",
          firm: "all",
          firmLabel: "",
          contact: "all",
          page: "",
        });
      }
      return;
    }

    if (key === "practice") {
      if (filters.publication === "all" || (filters.region === "all" && !noRegionsForPublication)) {
        setFilters(prev => ({ ...prev, practice: "all" }));
        updateUrl({ practice: "all" });
      } else {
        setSelectedFirmName(null);
        setSelectedContactName(null);
        setFilters(prev => ({
          ...prev,
          practice: stringValue,
          firm: "all",
          contact: "all",
        }));
        updateUrl({
          practice: stringValue,
          firm: "all",
          firmLabel: "",
          contact: "all",
          page: "",
        });
      }
      return;
    }

    if (key === "firm") {
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({ ...prev, firm: "all", contact: "all" }));
      updateUrl({ firm: "all", firmLabel: "", contact: "all", page: "" });
      return;
    }

    if (key === "contact") {
      setSelectedContactName(null);
      setCurrentPage(1);
      setFilters(prev => ({ ...prev, contact: stringValue }));
      updateUrl({ contact: stringValue, page: "" });
      return;
    }

    handleFilterChange(key, value, { page: "" });
  };

  const handleClearFilterWithDeps = (key: string) => {
    setCurrentPage(1);

    if (key === "edition") {
      const editionValue = String(currentYear);
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        edition: editionValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
      }));
      updateUrl({
        edition: editionValue,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        page: "",
      });
      return;
    }

    if (key === "publication") {
      setLegacyNoRegionsForPublication(false);
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
      }));
      updateUrl({
        publication: "all",
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        page: "",
      });
      return;
    }

    if (key === "region") {
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({
        ...prev,
        region: "all",
        practice: "all",
        firm: "all",
        contact: "all",
      }));
      updateUrl({
        region: "all",
        practice: "all",
        firm: "all",
        firmLabel: "",
        contact: "all",
        page: "",
      });
      return;
    }

    if (key === "practice") {
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({ ...prev, practice: "all", firm: "all", contact: "all" }));
      updateUrl({ practice: "all", firm: "all", firmLabel: "", contact: "all", page: "" });
      return;
    }

    if (key === "firm") {
      setSelectedFirmName(null);
      setSelectedContactName(null);
      setFilters(prev => ({ ...prev, firm: "all", contact: "all" }));
      updateUrl({ firm: "all", firmLabel: "", contact: "all", page: "" });
      return;
    }

    if (key === "contact") {
      setSelectedContactName(null);
      setFilters(prev => ({ ...prev, contact: "all" }));
      updateUrl({ contact: "all", page: "" });
      return;
    }

    if (key === "search") {
      setFilters(prev => ({ ...prev, search: "" }));
      updateUrl({ search: "", page: "" });
      return;
    }

    handleClearFilter?.(key, { page: "" });
  };

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, publication: "all", region: "all", practice: "all" }));
    updateUrl({
      [TAXONOMY_URL_PARAM]: next,
      publication: "all",
      region: "all",
      practice: "all",
      page: "",
    });
  };

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [];
    for (let year = currentYear + 1; year >= 2017; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const publicationOptions = useMemo(() => {
    const base = [{ value: "all", label: "All publications" }];
    if (mode === "new") {
      if (publicationUniquesError) return [{ value: "all", label: "Failed to load publications" }];
      const year = filters.edition.toLowerCase();
      return [
        ...base,
        ...(publicationUniques?.values ?? [])
          .filter(value => value.edition === null || value.edition.toLowerCase().includes(year))
          .map(value => ({
            value: value.editionId,
            label: publicationOptionLabel({ name: value.publicationName, edition: value.edition }),
          })),
      ];
    }
    const pubs = publicationsData?.publications ?? [];
    return [
      ...base,
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication
          ? `${p.publication} (${p.edition ?? "n/a"})`
          : `Publication ${p.id}${p.edition ? ` (${p.edition})` : ""}`,
      })),
    ];
  }, [
    mode,
    filters.edition,
    publicationUniques?.values,
    publicationUniquesError,
    publicationsData?.publications,
  ]);

  const regionOptions = useMemo(() => {
    if (mode !== "new") return regionOptionsState;
    if (newEditionId === undefined) return [{ value: "all", label: "Select publication first" }];
    if (regionUniquesError) return [{ value: "all", label: "Failed to load regions" }];
    return [
      { value: "all", label: "All regions" },
      ...(regionUniques?.values ?? []).map(value => ({
        value: value.rankingEntityId,
        label: value.regionName,
      })),
    ];
  }, [mode, newEditionId, regionUniques?.values, regionUniquesError, regionOptionsState]);

  const practiceOptions = useMemo(() => {
    if (mode !== "new") return practiceOptionsState;
    if (
      newEditionId === undefined ||
      (newRankingEntityId === undefined && !noRegionsForPublication)
    ) {
      return [{ value: "all", label: "Select region first" }];
    }
    if (practiceUniquesError) return [{ value: "all", label: "Failed to load practices" }];
    return [
      { value: "all", label: "All practices" },
      ...(practiceUniques?.values ?? []).map(value => ({
        value: value.bpaId,
        label: value.practiceAreaName,
      })),
    ];
  }, [
    mode,
    newEditionId,
    newRankingEntityId,
    noRegionsForPublication,
    practiceUniques?.values,
    practiceUniquesError,
    practiceOptionsState,
  ]);

  const handleFirmSearch = useCallback(async (query: string) => {
    const companies = await firmsService.getAllBySearch(query);
    const options = companies.map(c => ({
      value: c.companyId,
      label: c.name,
    }));
    firmOptionsRef.current = options;
    return options;
  }, []);

  const handleContactSearch = useCallback(async (query: string) => {
    const result = await contactsService.getAll({ search: query, limit: 50 });
    return result.contacts.map(c => ({ value: c.name, label: c.name }));
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: "all", label: "All Statuses" },
      { value: "APPROVED", label: "Approved" },
      { value: "REJECTED", label: "Rejected" },
      { value: "SUSPENDED", label: "Suspended" },
    ],
    [],
  );

  const languageOptions = useMemo(() => {
    const langs = languagesData?.languages ?? [];
    return [
      { value: "all", label: "All Languages" },
      ...langs.map(lang => ({ value: lang, label: lang.toUpperCase() })),
    ];
  }, [languagesData]);

  const sentimentOptions = useMemo(
    () => [
      { value: "all", label: "All sentiments" },
      { value: "positive", label: "Positive" },
      { value: "negative", label: "Negative" },
      { value: "neutral", label: "Neutral" },
    ],
    [],
  );

  const FILTER_CONFIGS_ROW_1 = [
    {
      key: "edition",
      label: "Edition",
      options: editionOptions,
      preserveOptionOrder: true,
    },
    {
      key: "publication",
      label: "Publication",
      options: publicationOptions,
    },
    {
      key: "region",
      label: "Region",
      options: regionOptions,
    },
    {
      key: "practice",
      label: "Practice",
      options: practiceOptions,
    },
  ];

  const FILTER_CONFIGS_ROW_2 = [
    {
      key: "status",
      label: "Status",
      options: statusOptions,
    },
    {
      key: "language",
      label: "Language",
      options: languageOptions,
    },
    {
      key: "sentiment",
      label: "Sentiment",
      options: sentimentOptions,
    },
  ];

  const enhancedActiveFilters = useMemo(() => {
    return activeFilters.map(filter => {
      if (filter.key === "publication") {
        const match = publicationOptions.find(opt => String(opt.value) === filters.publication);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "region") {
        const match = regionOptions.find(opt => String(opt.value) === filters.region);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "practice") {
        const match = practiceOptions.find(opt => String(opt.value) === filters.practice);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "firm") {
        return {
          ...filter,
          displayValue: selectedFirmName ?? String(filter.value),
        } as const;
      }
      if (filter.key === "contact") {
        return {
          ...filter,
          displayValue: selectedContactName ?? String(filter.value),
        } as const;
      }
      return { ...filter, displayValue: filter.displayValue ?? String(filter.value) } as const;
    });
  }, [
    activeFilters,
    filters.publication,
    filters.region,
    filters.practice,
    filters.firm,
    filters.contact,
    publicationOptions,
    regionOptions,
    practiceOptions,
    selectedFirmName,
    selectedContactName,
  ]);

  const [savingOrderIds, setSavingOrderIds] = useState<string[]>([]);

  const handleUnlinkLawyer = useCallback(
    async (row: Testimonial, testimonialType: UnlinkTestimonialType | undefined) => {
      try {
        await pipelineService.unlinkLawyerFromTestimonial(row.id, testimonialType);
        setReloadToken(token => token + 1);
        setToast({ message: "Lawyer removed from testimonial", type: "success" });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to remove lawyer";
        setToast({ message: `Request failed: ${msg}`, type: "error" });
      }
    },
    [],
  );

  const handleOrderChange = useCallback(async (row: Testimonial, displayOrder: number | null) => {
    const setOrder = (ids: string[], value: number | null) =>
      setData(prev =>
        prev
          ? {
              ...prev,
              items: prev.items.map(item =>
                ids.includes(item.id) ? { ...item, displayOrder: value } : item,
              ),
            }
          : prev,
      );

    const confirmed = row.displayOrder;
    setSavingOrderIds(ids => [...ids, row.id]);
    setOrder([row.id], displayOrder);
    try {
      await pipelineService.updateTestimonial(row.id, { displayOrder });
      if (!row.isTranslation) {
        setOrder(
          (row.relatedTestimonials ?? []).map(related => related.id),
          displayOrder,
        );
      }
      setToast({ message: "Order saved", type: "success" });
    } catch (err) {
      setOrder([row.id], confirmed);
      const msg = err instanceof Error ? err.message : "Failed to save order";
      setToast({ message: `Request failed: ${msg}`, type: "error" });
    } finally {
      setSavingOrderIds(ids => ids.filter(id => id !== row.id));
    }
  }, []);

  const columns: Column<Testimonial>[] = useMemo(
    () => [
      {
        header: "CONTENT",
        accessor: "content" as keyof Testimonial,
        render: (_value: unknown, row: Testimonial) => (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span>
              {row.content.length > 150 ? `${row.content.slice(0, 150)}...` : row.content}
            </span>
            {row.lawyerName && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "var(--color-text-secondary)",
                  fontStyle: "italic",
                }}
              >
                {row.lawyerName}
              </span>
            )}
          </div>
        ),
      },
      {
        header: "DETAILS",
        accessor: "firmRef" as keyof Testimonial,
        render: (_value: unknown, row: Testimonial) => (
          <DetailsColumn
            item={row}
            onLinkContact={() => setLinkingContactItem(row)}
            onUnlinkLawyer={
              canUnlinkLawyer && (row.contactId != null || !!row.lawyerName)
                ? type => handleUnlinkLawyer(row, type)
                : undefined
            }
          />
        ),
      },
      {
        header: "STATUS",
        accessor: "status" as keyof Testimonial,
        render: (_value: unknown, row: Testimonial) => <StatusColumn item={row} />,
      },
      {
        header: "ORDER",
        accessor: "displayOrder" as keyof Testimonial,
        render: (_value: unknown, row: Testimonial) => (
          <OrderColumn
            item={row}
            canEdit={canEditOrder}
            saving={savingOrderIds.includes(row.id)}
            onChange={handleOrderChange}
          />
        ),
      },
    ],
    [canEditOrder, savingOrderIds, handleOrderChange, canUnlinkLawyer, handleUnlinkLawyer],
  );

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Approved Testimonials">
        {canExport && (
          <div className={styles.headerActions}>
            <Checkbox
              id="testimonials-export-visible-only"
              label="Visible on legal500.com only"
              aria-label="Visible on legal500.com only"
              checked={visibleOnly}
              disabled={isExporting}
              onChange={event => setVisibleOnly(event.target.checked)}
            />
            <Button
              variant="outlined"
              color="default"
              onClick={handleExport}
              disabled={!exportRequest || isExporting}
              aria-describedby={exportNeedsSelection ? "testimonials-export-hint" : undefined}
            >
              {isExporting ? "Exporting..." : "Export"}
            </Button>
            {exportNeedsSelection && (
              <span id="testimonials-export-hint" className={styles.exportHint}>
                Select a firm and publication to export
              </span>
            )}
          </div>
        )}
      </PageHeader>

      {exportError && (
        <ErrorBanner error={exportError} onRetry={handleExport} loading={isExporting} />
      )}

      <div className={styles.filtersContainer}>
        {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}
        <div className={styles.filtersRow}>
          {FILTER_CONFIGS_ROW_1.map(filter => {
            let className = styles.filterDropdown;
            if (filter.key === "edition") {
              className = `${styles.filterDropdown} ${styles.filterDropdownSmall}`;
            } else if (filter.key === "publication") {
              className = `${styles.filterDropdown} ${styles.filterDropdownWide}`;
            } else if (filter.key === "practice") {
              className = `${styles.filterDropdown} ${styles.filterDropdownPractice}`;
            }
            const filterKey = filter.key as keyof typeof filters;
            const filterValue = filters[filterKey];
            return (
              <div key={filter.key} className={className}>
                <SearchableSelect
                  name={filter.key}
                  label={filter.label}
                  value={
                    typeof filterValue === "string" || typeof filterValue === "number"
                      ? filterValue
                      : String(filterValue)
                  }
                  options={filter.options}
                  preserveOptionOrder={
                    "preserveOptionOrder" in filter && filter.preserveOptionOrder
                  }
                  onChange={value =>
                    value === null && filter.key === "edition"
                      ? handleClearFilterWithDeps("edition")
                      : handleFilterChangeWithDeps(filter.key, value ?? "all")
                  }
                />
              </div>
            );
          })}
        </div>
        <div className={styles.filtersRow}>
          <div className={styles.filterDropdown}>
            <AsyncSearchableSelect
              name="firm"
              label="Firm"
              value={filters.firm === "all" ? null : Number(filters.firm)}
              onChange={value => {
                if (value == null) {
                  setSelectedFirmName(null);
                  setSelectedContactName(null);
                  setCurrentPage(1);
                  setFilters(prev => ({ ...prev, firm: "all", contact: "all" }));
                  updateUrl({ firm: "all", firmLabel: "", contact: "all", page: "" });
                } else {
                  const firmLabel =
                    firmOptionsRef.current.find(o => o.value === value)?.label ?? "";
                  setSelectedFirmName(firmLabel);
                  setSelectedContactName(null);
                  setCurrentPage(1);
                  setFilters(prev => ({ ...prev, firm: String(value), contact: "all" }));
                  updateUrl({ firm: String(value), firmLabel, contact: "all", page: "" });
                }
              }}
              onOptionChange={option => setSelectedFirmName(option?.label ?? null)}
              onSearch={handleFirmSearch}
              placeholder="Search firms..."
              minSearchLength={2}
              initialOption={
                filters.firm !== "all" && selectedFirmName
                  ? { value: Number(filters.firm), label: selectedFirmName }
                  : null
              }
            />
          </div>
          <div className={styles.filterDropdown}>
            <AsyncSearchableSelect
              name="contact"
              label="Contact"
              value={filters.contact === "all" ? null : filters.contact}
              onChange={value => {
                if (value == null) setSelectedContactName(null);
                handleFilterChangeWithDeps("contact", value != null ? String(value) : "all");
              }}
              onOptionChange={option => setSelectedContactName(option?.label ?? null)}
              onSearch={handleContactSearch}
              placeholder="Search contacts..."
              minSearchLength={1}
              initialOption={
                filters.contact !== "all"
                  ? { value: filters.contact, label: filters.contact }
                  : null
              }
            />
          </div>
          {/* Content type filter rendered directly after Contact */}
          <div className={styles.filterDropdown}>
            <SearchableSelect
              name="type"
              label="Content type"
              value={filters.type}
              options={contentTypeOptions}
              onChange={value => handleFilterChangeWithDeps("type", value ?? "all")}
            />
          </div>
          {FILTER_CONFIGS_ROW_2.map(filter => {
            const filterKey = filter.key as keyof typeof filters;
            const filterValue = filters[filterKey];
            return (
              <div key={filter.key} className={styles.filterDropdown}>
                <SearchableSelect
                  name={filter.key}
                  label={filter.label}
                  value={
                    typeof filterValue === "string" || typeof filterValue === "number"
                      ? filterValue
                      : String(filterValue)
                  }
                  options={filter.options}
                  onChange={value => handleFilterChangeWithDeps(filter.key, value ?? "all")}
                />
              </div>
            );
          })}
          <div className={styles.filterDropdown}>
            <SearchBar
              value={filters.search}
              onChange={value => handleTextFilterChange("search", value)}
              onClear={() => handleTextFilterChange("search", "")}
              placeholder="Search content..."
            />
          </div>
        </div>
      </div>

      <div className={styles.filtersAndPaginationRow}>
        {enhancedActiveFilters.length > 0 && (
          <div className={styles.activeFilters}>
            <span className={styles.activeFiltersLabel}>Active filters:</span>
            <div className={styles.activeFiltersChips}>
              {enhancedActiveFilters.map(filter => (
                <Chip
                  key={filter.key}
                  size="small"
                  variant="outlined"
                  className={styles.filterChip}
                  onDelete={() => handleClearFilterWithDeps(filter.key)}
                >
                  {filter.label}: {filter.displayValue || filter.value}
                </Chip>
              ))}
            </div>
          </div>
        )}
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={e => {
              const newPageSize = Number(e.target.value);
              setItemsPerPage(newPageSize);
              setCurrentPage(1);
              updateUrl({ pageSize: String(newPageSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      {loading && <LoadingState message="Loading testimonials..." />}

      {error && !loading && (
        <EmptyState
          variant="error"
          title="Failed to load testimonials"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => setReloadToken(token => token + 1),
          }}
        />
      )}

      {!loading && !error && (
        <>
          <div className={styles.tableContainer}>
            <div className={styles.scopedTable}>
              <DataTable
                data={testimonials}
                columns={columns}
                onRowClick={handleRowClick}
                emptyMessage="No testimonials found matching your criteria."
              />
            </div>
          </div>

          {pagination.total > 0 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={Math.ceil(pagination.total / itemsPerPage)}
              startIndex={(currentPage - 1) * itemsPerPage + 1}
              endIndex={Math.min(currentPage * itemsPerPage, pagination.total)}
              totalCount={pagination.total}
              itemName="testimonials"
              onPageChange={page => {
                setCurrentPage(page);
                updateUrl({ page: String(page) });
              }}
            />
          )}
        </>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}

      {linkingContactItem && (
        <LinkContactModal
          item={linkingContactItem}
          onClose={() => setLinkingContactItem(null)}
          onLinked={() => {
            setLinkingContactItem(null);
            setReloadToken(token => token + 1);
          }}
        />
      )}
    </div>
  );
}

export default TestimonialsPage;
